import { Controller, useForm } from "react-hook-form";
import { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button2 } from "@/components/UI2/atoms/Button";
import { Checkbox2 } from "@/components/UI2/atoms/Checkbox";
import type { CosignatureDocumentType } from "@/core/types";
import { FieldError2 } from "@/components/UI2/atoms/FieldError";
import { ModalControlled2, ModalDivider } from "@/components/UI2/molecules/Modal";
import { useCosignDocument } from "@/features/cosignature/api/mutations/useCosignDocument";

import { approveCosignSchema, type ApproveCosignFormValues } from "./schema";

type CosignNoteModalProps = {
  documentId: string;
  documentType: CosignatureDocumentType;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: VoidFunction;
  patientId: string;
  providerName: string;
};

export function CosignNoteModal({
  documentId,
  documentType,
  isOpen,
  onClose,
  onSuccess,
  patientId,
  providerName,
}: CosignNoteModalProps) {
  const { control, formState, handleSubmit, reset } = useForm<ApproveCosignFormValues>({
    defaultValues: { isConfirmed: false },
    mode: "onChange",
    resolver: zodResolver(approveCosignSchema),
  });

  const { isPending, mutate } = useCosignDocument({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <ModalControlled2 isOpen={isOpen} onOpenChange={handleClose} title="Approve & Co-Sign">
      <form
        className="tw:flex tw:flex-col tw:gap-4"
        onSubmit={handleSubmit(() => mutate({ documentId, documentType, patientId }))}
      >
        <p className="tw:text-text-secondary">
          {documentType === "note_addendum" ? (
            <>
              You are about to co-sign <b>{providerName}&apos;s</b> addendum.
            </>
          ) : (
            <>
              You are about to co-sign <b>{providerName}&apos;s</b> note. This action is final and
              will mark the note as billable.
            </>
          )}
        </p>

        <Controller
          control={control}
          name="isConfirmed"
          render={({ field, fieldState }) => (
            <div className="tw:rounded-sm tw:border tw:border-stroke-tertiary tw:px-3 tw:py-2">
              <Checkbox2
                className="tw:text-text-secondary"
                isInvalid={!!fieldState.error}
                isSelected={field.value}
                onChange={field.onChange}
              >
                {documentType === "note_addendum"
                  ? "I confirm I have reviewed this addendum and it is ready for co-signature"
                  : "I confirm I have reviewed this note and it is ready for co-signature"}
              </Checkbox2>
              {fieldState.error && <FieldError2>{fieldState.error.message}</FieldError2>}
            </div>
          )}
        />

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2 onPress={handleClose} variant="secondary">
            Cancel
          </Button2>

          <Button2
            isDisabled={!formState.isValid}
            isLoading={isPending}
            type="submit"
            variant="primary"
          >
            Approve &amp; Co-Sign
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}
